import { RISK_LIST_TYPES, RISK_MATCH_TYPES } from "@platform/domain";
import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { ListingKind, ProductBlock, ProductDraft, ProductStatus } from "./listing.js";
/** Product review, blocking, and the risk lists (CAT-4, CAT-5, ADM-2, card 038). */
const Catalog = { surface: "operator", auth: "operator", permission: "ops:catalog.manage" };
const Risk = { surface: "operator", auth: "operator", permission: "ops:risk.manage" };
const Reason = z.string().trim().min(3).max(2000);
const ReviewId = z.object({ reviewId: Id });
export const RiskListType = z.enum(RISK_LIST_TYPES).meta({ id: "RiskListType" });
export const RiskMatchType = z
    .enum(RISK_MATCH_TYPES)
    .meta({ id: "RiskMatchType", description: "An HS-code prefix (goods only), a keyword, or a category id" });
/** In paths, list types are kebab-case. */
export const RISK_LIST_PATHS = { prohibited: "prohibited", "export-control": "export_control" };
const RiskListPath = z.enum(["prohibited", "export-control"]);
/** The check of the published products against a new entry (card 075). */
export const RiskEntryScan = z
    .object({
    state: z.enum(["running", "done"]),
    productsChecked: z.number().int().nonnegative().nullable().describe("Set when done"),
    openMatches: z.number().int().nonnegative(),
    finishedAt: Timestamp.nullable(),
})
    .meta({ id: "RiskEntryScan" });
export const RiskEntryView = z
    .object({
    id: Id,
    listType: RiskListType,
    matchType: RiskMatchType,
    value: z.string(),
    note: z.string().nullable(),
    createdByOperatorId: Id,
    createdAt: Timestamp,
})
    .meta({ id: "RiskEntry" });
/** An entry as its list shows it, with the check of the published products against it. */
export const RiskListEntry = RiskEntryView.extend({
    scan: RiskEntryScan.nullable().describe("Null for an entry added before published products were checked"),
}).meta({ id: "RiskListEntry" });
export const OpsProduct = z
    .object({
    id: Id,
    shopId: Id,
    enterpriseId: Id,
    categoryId: Id,
    listingKind: ListingKind,
    name: LocalizedText,
    slug: z.string().nullable(),
    status: ProductStatus,
    block: ProductBlock.nullable(),
    publishedAt: Timestamp.nullable(),
    updatedAt: Timestamp,
})
    .meta({ id: "OpsProduct" });
export const OpsProductReview = z
    .object({
    id: Id,
    product: OpsProduct,
    revisionId: Id.nullable().describe("Set when the review is of edits to a published product"),
    reason: z.enum(["list_match", "manual"]),
    matchedEntryIds: z.array(Id),
    matchedEntries: z.array(RiskEntryView).describe("The matched entries still on the lists"),
    assigneeOperatorId: Id.nullable(),
    decision: z.enum(["approved", "rejected"]).nullable(),
    decisionReason: z.string().nullable(),
    decidedByOperatorId: Id.nullable(),
    decidedAt: Timestamp.nullable(),
    createdAt: Timestamp,
})
    .meta({ id: "OpsProductReview" });
export const OpsProductListItem = OpsProduct.extend({
    shop: z.object({ id: Id, slug: z.string(), name: LocalizedText }),
}).meta({ id: "OpsProductListItem" });
export const RiskMatchState = z.enum(["open", "blocked", "kept", "entry_removed"]).meta({
    id: "RiskMatchState",
    description: "Open until the product is blocked, kept, or the entry removed",
});
/** A published product a new entry matches; the product stays as it is until an operator acts (card 075). */
export const RiskMatchView = z
    .object({
    id: Id,
    entry: z
        .object({ id: Id, listType: RiskListType, matchType: RiskMatchType, value: z.string() })
        .describe("The entry's terms, kept after it is removed"),
    product: OpsProductListItem.describe("The product as it is now"),
    state: RiskMatchState,
    matchedAt: Timestamp,
    resolution: z
        .object({ operatorId: Id, at: Timestamp, reason: z.string().nullable() })
        .nullable()
        .describe("Who closed the match, when, and why: a block's reason, a keep's, or none when the entry was removed"),
})
    .meta({ id: "RiskMatch" });
export const OpsProductReviewDetail = OpsProductReview.extend({
    content: ProductDraft.describe("What is reviewed: the product, or the edits"),
    published: ProductDraft.nullable().describe("For edits, the product as the public sees it now"),
    images: z
        .array(z.object({ fileId: Id, url: z.url() }))
        .describe("Links valid for five minutes to the images of the content and of the published product, which are private until approved"),
}).meta({ id: "OpsProductReviewDetail" });
export const listProductReviews = defineRoute({
    ...Catalog,
    id: "ops.catalog.reviews.list",
    method: "GET",
    path: "/v1/ops/product-reviews",
    marks: [],
    summary: "The product review queue, oldest first, or decided reviews, newest first",
    prd: ["CAT-5", "ADM-2"],
    request: {
        query: z.object({
            state: z.enum(["open", "decided"]).default("open"),
            assignee: z.enum(["any", "me", "unassigned"]).default("any"),
            limit: z.coerce.number().int().min(1).max(100).default(50),
        }),
    },
    responses: { 200: { description: "Reviews", schema: z.object({ items: z.array(OpsProductReview) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const getProductReview = defineRoute({
    ...Catalog,
    id: "ops.catalog.reviews.get",
    method: "GET",
    path: "/v1/ops/product-reviews/{reviewId}",
    marks: [],
    summary: "One review with the listing under review, and for edits the product as published",
    prd: ["CAT-5", "ADM-2"],
    request: { params: ReviewId },
    responses: { 200: { description: "The review", schema: OpsProductReviewDetail } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const assignProductReview = defineRoute({
    ...Catalog,
    id: "ops.catalog.reviews.assign",
    method: "POST",
    path: "/v1/ops/product-reviews/{reviewId}/assign",
    marks: [],
    summary: "Take a review, or give it to another operator",
    prd: ["ADM-2"],
    request: {
        params: ReviewId,
        body: z.object({ operatorId: Id.optional().describe("Defaults to the caller") }),
    },
    responses: { 200: { description: "The review", schema: OpsProductReview } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const decideProductReview = defineRoute({
    ...Catalog,
    id: "ops.catalog.reviews.decide",
    method: "POST",
    path: "/v1/ops/product-reviews/{reviewId}/decide",
    marks: [],
    summary: "Approve (the product goes live, or the edits apply) or reject (the product returns to draft, or the edits are dropped), always with a reason the seller sees",
    prd: ["CAT-5", "ADM-2"],
    request: {
        params: ReviewId,
        body: z.object({ decision: z.enum(["approve", "reject"]), reason: Reason }),
    },
    responses: { 200: { description: "The decided review", schema: OpsProductReview } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const listOpsProducts = defineRoute({
    ...Catalog,
    id: "ops.catalog.products.list",
    method: "GET",
    path: "/v1/ops/products",
    marks: [],
    summary: "Look products up by name (in either language), product slug, or the shop's name or slug, most recently changed first",
    prd: ["CAT-4", "ADM-2"],
    request: {
        query: z.object({
            q: z.string().trim().min(1).max(100).optional(),
            status: ProductStatus.optional(),
            limit: z.coerce.number().int().min(1).max(100).default(50),
        }),
    },
    responses: { 200: { description: "Products", schema: z.object({ items: z.array(OpsProductListItem) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const blockProduct = defineRoute({
    ...Catalog,
    id: "ops.catalog.products.block",
    method: "POST",
    path: "/v1/ops/products/{productId}/block",
    marks: [],
    summary: "Block a live or off-shelf product at once, with a reason the seller sees; it leaves public pages and search, and its open inquiries close",
    prd: ["CAT-4"],
    request: { params: z.object({ productId: Id }), body: z.object({ reason: Reason }) },
    responses: { 200: { description: "The product", schema: OpsProduct } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const unblockProduct = defineRoute({
    ...Catalog,
    id: "ops.catalog.products.unblock",
    method: "POST",
    path: "/v1/ops/products/{productId}/unblock",
    marks: [],
    summary: "Lift a block with a reason; the product comes back off shelf, and the seller relists it",
    prd: ["CAT-4"],
    request: { params: z.object({ productId: Id }), body: z.object({ reason: Reason }) },
    responses: { 200: { description: "The product", schema: OpsProduct } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const listRiskEntries = defineRoute({
    ...Risk,
    id: "ops.risk.entries.list",
    method: "GET",
    path: "/v1/ops/risk-lists/{listType}/entries",
    marks: [],
    summary: "A list's entries, newest first",
    prd: ["CAT-5"],
    request: { params: z.object({ listType: RiskListPath }) },
    responses: { 200: { description: "Entries", schema: z.object({ items: z.array(RiskListEntry) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const createRiskEntry = defineRoute({
    ...Risk,
    id: "ops.risk.entries.create",
    method: "POST",
    path: "/v1/ops/risk-lists/{listType}/entries",
    marks: [],
    summary: "Add an entry: an HS-code prefix of 2 to 10 digits, a keyword matched as a whole word (anywhere in Chinese), or a category and all below it. Applies to submissions from now on; the published products are checked against it in the background, and none is changed",
    prd: ["CAT-5"],
    request: {
        params: z.object({ listType: RiskListPath }),
        body: z.object({
            matchType: RiskMatchType,
            value: z.string().trim().min(1).max(100),
            note: z.string().trim().max(500).nullable().default(null),
        }),
    },
    responses: { 201: { description: "The entry", schema: RiskListEntry } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const deleteRiskEntry = defineRoute({
    ...Risk,
    id: "ops.risk.entries.delete",
    method: "DELETE",
    path: "/v1/ops/risk-lists/{listType}/entries/{entryId}",
    marks: [],
    summary: "Remove an entry; reviews it opened keep its id, and its open matches close",
    prd: ["CAT-5"],
    request: { params: z.object({ listType: RiskListPath, entryId: Id }) },
    responses: { 204: { description: "Removed" } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const listRiskEntryMatches = defineRoute({
    ...Risk,
    id: "ops.risk.entries.matches",
    method: "GET",
    path: "/v1/ops/risk-lists/{listType}/entries/{entryId}/matches",
    marks: [],
    summary: "The published products a new entry matched, open ones first; also for an entry since removed. Nothing is changed on a product until an operator blocks or keeps it",
    prd: ["CAT-5", "ADM-2"],
    request: { params: z.object({ listType: RiskListPath, entryId: Id }) },
    responses: { 200: { description: "Matches", schema: z.object({ items: z.array(RiskMatchView) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const listRiskMatches = defineRoute({
    ...Risk,
    id: "ops.risk.matches.list",
    method: "GET",
    path: "/v1/ops/risk-matches",
    marks: [],
    summary: "Matches across every entry, the open queue oldest first by default",
    prd: ["CAT-5", "ADM-2"],
    request: {
        query: z.object({
            state: RiskMatchState.default("open"),
            limit: z.coerce.number().int().min(1).max(200).default(100),
        }),
    },
    responses: { 200: { description: "Matches", schema: z.object({ items: z.array(RiskMatchView) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const keepRiskMatch = defineRoute({
    ...Risk,
    id: "ops.risk.matches.keep",
    method: "POST",
    path: "/v1/ops/risk-matches/{matchId}/keep",
    marks: [],
    summary: "Keep a matched product as it is, with a reason, which is audited on the product",
    prd: ["CAT-5"],
    request: { params: z.object({ matchId: Id }), body: z.object({ reason: Reason }) },
    responses: { 200: { description: "The match", schema: RiskMatchView } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const opsModerationRoutes = [
    listProductReviews,
    getProductReview,
    assignProductReview,
    decideProductReview,
    listOpsProducts,
    blockProduct,
    unblockProduct,
    listRiskEntries,
    createRiskEntry,
    deleteRiskEntry,
    listRiskEntryMatches,
    listRiskMatches,
    keepRiskMatch,
];
//# sourceMappingURL=ops-moderation.routes.js.map